import type { Role } from '../shared/domain';
import {
  changePassword,
  createUser,
  downloadWorkbook,
  fetchUsers,
  logout,
  patchUser,
  type AuthUser,
  type UserAccount,
} from './api';
import { el, textInput, toast } from './ui';

export function renderAccount(
  root: HTMLElement,
  user: AuthUser
): () => void {
  const host = el('div', { className: 'stack' });
  root.append(
    el('h1', { textContent: 'Account' }),
    el('p', { className: 'lead', textContent: user.displayName }),
    el('p', { className: 'muted', textContent: user.role === 'admin' ? 'Admin' : 'Instructor' }),
    host
  );

  const signOut = el('button', { type: 'button', className: 'btn', textContent: 'Sign out' });
  signOut.addEventListener('click', () => {
    void logout().finally(() => {
      window.dispatchEvent(new CustomEvent('mdrc-unauthorized'));
    });
  });

  const current = textInput({ type: 'password', placeholder: 'Current password' });
  const next = textInput({ type: 'password', placeholder: 'New password' });
  const pwError = el('p', { className: 'form-error' });
  const pwSave = el('button', { type: 'button', className: 'btn', textContent: 'Update password' });
  pwSave.addEventListener('click', () => {
    void (async () => {
      pwError.textContent = '';
      pwSave.disabled = true;
      try {
        await changePassword(current.value, next.value);
        current.value = '';
        next.value = '';
        toast('Password updated', 'ok');
      } catch (err) {
        pwError.textContent = (err as Error).message;
      } finally {
        pwSave.disabled = false;
      }
    })();
  });

  host.append(
    signOut,
    el('details', { className: 'advanced' }, [
      el('summary', { textContent: 'Change password' }),
      current,
      next,
      pwError,
      pwSave,
    ])
  );

  if (user.role === 'admin') {
    const exportBtn = el('button', {
      type: 'button',
      className: 'btn btn-primary',
      textContent: 'Download workbook',
    });
    exportBtn.addEventListener('click', () => {
      void (async () => {
        exportBtn.disabled = true;
        try {
          await downloadWorkbook();
        } catch (err) {
          toast((err as Error).message, 'err');
        } finally {
          exportBtn.disabled = false;
        }
      })();
    });
    const usersHost = el('div', { className: 'stack' });
    host.append(exportBtn, el('h2', { textContent: 'Logins' }), usersHost);
    void loadUsers(usersHost);
  }

  return () => undefined;
}

async function loadUsers(host: HTMLElement): Promise<void> {
  host.replaceChildren(el('p', { className: 'muted', textContent: 'Loading…' }));
  try {
    const users = await fetchUsers();
    paintUsers(host, users);
  } catch (err) {
    host.replaceChildren(el('p', { className: 'form-error', textContent: (err as Error).message }));
  }
}

function paintUsers(host: HTMLElement, users: UserAccount[]): void {
  const username = textInput({ placeholder: 'Username' });
  const display = textInput({ placeholder: 'Name' });
  const password = textInput({ type: 'password', placeholder: 'Password' });
  let role: Role = 'instructor';
  const roles = el('div', { className: 'chips' });
  const paintRoles = () => {
    roles.replaceChildren();
    for (const item of [
      { id: 'instructor' as const, label: 'Instructor' },
      { id: 'admin' as const, label: 'Admin' },
    ]) {
      const on = role === item.id;
      const btn = el('button', {
        type: 'button',
        className: on ? 'chip on' : 'chip',
        textContent: item.label,
        'aria-pressed': on ? 'true' : 'false',
      });
      btn.addEventListener('click', () => {
        role = item.id;
        paintRoles();
      });
      roles.append(btn);
    }
  };
  paintRoles();
  const error = el('p', { className: 'form-error' });
  const add = el('button', { type: 'button', className: 'btn btn-primary', textContent: 'Add login' });
  add.addEventListener('click', () => {
    void (async () => {
      error.textContent = '';
      add.disabled = true;
      try {
        await createUser({
          username: username.value.trim().toLowerCase(),
          display_name: display.value.trim(),
          password: password.value,
          role,
        });
        toast('Login added', 'ok');
        await loadUsers(host);
      } catch (err) {
        error.textContent = (err as Error).message;
        add.disabled = false;
      }
    })();
  });

  const list = el('div', { className: 'stack' });
  for (const account of users) {
    list.append(userRow(account, host));
  }

  host.replaceChildren(
    el('div', { className: 'card' }, [
      username,
      display,
      password,
      roles,
      error,
      add,
    ]),
    list
  );
}

function userRow(account: UserAccount, host: HTMLElement): HTMLElement {
  const row = el('div', { className: 'card' });
  row.append(
    el('strong', { textContent: account.displayName }),
    el('p', {
      className: 'muted',
      textContent: `${account.username} · ${account.role}${account.active ? '' : ' · disabled'}`,
    })
  );
  const toggle = el('button', {
    type: 'button',
    className: 'btn',
    textContent: account.active ? 'Disable' : 'Enable',
  });
  toggle.addEventListener('click', () => {
    void (async () => {
      try {
        await patchUser(account.id, { active: !account.active });
        await loadUsers(host);
      } catch (err) {
        toast((err as Error).message, 'err');
      }
    })();
  });
  const reset = textInput({ type: 'password', placeholder: 'New password' });
  const setPw = el('button', { type: 'button', className: 'btn', textContent: 'Set password' });
  setPw.addEventListener('click', () => {
    void (async () => {
      try {
        await patchUser(account.id, { password: reset.value });
        reset.value = '';
        toast('Password set', 'ok');
      } catch (err) {
        toast((err as Error).message, 'err');
      }
    })();
  });
  row.append(toggle, el('div', { className: 'inline' }, [reset, setPw]));
  return row;
}
